import React, { useState, useEffect } from "react";
import {
  Ticket,
  Users,
  FileText,
  Gift,
  Plus,
  X,
  Trash2,
  AlertCircle,
  CheckCircle2,
  Calculator,
  Edit2,
  Save,
  ChevronDown,
  Banknote,
} from "lucide-react";
import { toast } from "react-toastify";
import { CURRENCIES, formatPrice, getTicketCurrency } from "../../../../../utils/currency";

const TicketTypesSection = ({
  overallSlots,
  ticketTypes = [],
  onOverallSlotsChange,
  onTicketTypesChange,
}) => {
  const [newTicket, setNewTicket] = useState({
    name: "",
    // When "free", we force `price` to 0 in state so the payload stays consistent.
    pricingMode: "priced", // "priced" | "free"
    previousPricedPrice: "",
    price: "",
    currency: "GHS",
    slotsAllocated: "",
    description: "",
    whatsIncluded: [],
  });
  const [newIncludedItem, setNewIncludedItem] = useState("");
  const [editingIndex, setEditingIndex] = useState(null);
  const [currencyDropdownOpen, setCurrencyDropdownOpen] = useState(false);

  // Helper function to ensure valid currency
  const ensureCurrency = (currency) => {
    if (currency && (currency === "GHS" || currency === "USD")) {
      return currency;
    }
    return "GHS"; // Default to Ghana Cedis
  };

  // Normalize ticket types to ensure currency field exists (for backward compatibility)
  useEffect(() => {
    if (ticketTypes && ticketTypes.length > 0) {
      const needsNormalization = ticketTypes.some(ticket => !ticket.currency);
      if (needsNormalization) {
        const normalizedTickets = ticketTypes.map(ticket => ({
          ...ticket,
          currency: ensureCurrency(ticket.currency), // Ensure currency field exists
        }));
        // Only update if normalization was needed
        if (JSON.stringify(normalizedTickets) !== JSON.stringify(ticketTypes)) {
          onTicketTypesChange(normalizedTickets);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount to normalize existing tickets

  const totalAllocatedSlots = ticketTypes.reduce(
    (sum, ticket) => sum + (parseInt(ticket.slotsAllocated) || 0),
    0
  );

  const remainingSlots = (parseInt(overallSlots) || 0) - totalAllocatedSlots;
  const isSlotsBalanced = overallSlots && remainingSlots === 0;
  const hasSlots = overallSlots && parseInt(overallSlots) > 0;

  const handleInputChange = (field, value) => {
    if ((field === "price" || field === "slotsAllocated") && value) {
      if (field === "price") {
        const numericValue = value.replace(/[^0-9.]/g, "");
        const parts = numericValue.split(".");
        if (parts.length > 2) return;
        const nextPriceString =
          parts.length === 2 ? parts[0] + "." + parts[1] : parts[0];
        // Allow the user to clear the input without forcing tab changes.
        const nextPriceNumber =
          nextPriceString === "" ? NaN : parseFloat(nextPriceString);

        setNewTicket((prev) => {
          // Smart switch: if user is in "Priced" mode and types 0,
          // treat it as "Free" so payload always sends price=0.
          if (nextPriceNumber === 0 && prev.pricingMode === "priced") {
            return {
              ...prev,
              pricingMode: "free",
              previousPricedPrice: prev.price || prev.previousPricedPrice || "",
              price: "0",
            };
          }

          // Vice versa: if user is in "Free" mode and types > 0,
          // automatically switch back to Priced.
          if (
            prev.pricingMode === "free" &&
            !Number.isNaN(nextPriceNumber) &&
            nextPriceNumber > 0
          ) {
            return {
              ...prev,
              pricingMode: "priced",
              previousPricedPrice: "",
              price: nextPriceString,
            };
          }

          return {
            ...prev,
            [field]: nextPriceString,
          };
        });
      } else {
        const numericValue = value.replace(/[^0-9]/g, "");
        setNewTicket((prev) => ({
          ...prev,
          [field]: numericValue,
        }));
      }
    } else {
      setNewTicket((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  const setTicketPricingMode = (mode) => {
    setNewTicket((prev) => {
      if (mode === "free") {
        return {
          ...prev,
          pricingMode: "free",
          previousPricedPrice: prev.price || "",
          price: "0",
        };
      }

      return {
        ...prev,
        pricingMode: "priced",
        price: prev.previousPricedPrice || "",
        previousPricedPrice: "",
      };
    });
  };

  const handleAddIncludedItem = () => {
    const trimmedItem = newIncludedItem.trim();
    if (!trimmedItem) {
      toast.error("Please enter an item");
      return;
    }
    if (newTicket.whatsIncluded.includes(trimmedItem)) {
      toast.warning("This item is already in the list");
      return;
    }
    setNewTicket((prev) => ({
      ...prev,
      whatsIncluded: [...prev.whatsIncluded, trimmedItem],
    }));
    setNewIncludedItem("");
  };

  const handleRemoveIncludedItem = (index) => {
    setNewTicket((prev) => ({
      ...prev,
      whatsIncluded: prev.whatsIncluded.filter((_, i) => i !== index),
    }));
  };

  const handleAddTicket = () => {
    const trimmedName = newTicket.name.trim();
    const trimmedDescription = newTicket.description.trim();
    const isFree = newTicket.pricingMode === "free";
    const priceNumber = parseFloat(newTicket.price);

    if (!trimmedName) {
      toast.error("Please enter a ticket name");
      return;
    }

    if (isFree) {
      // Backend expects `price`; for free tickets we always send 0.
      if (isNaN(priceNumber) || priceNumber !== 0) {
        toast.error("Free tickets must have a price of 0");
        return;
      }
    } else {
      if (newTicket.price === "" || newTicket.price === null || newTicket.price === undefined) {
        toast.error("Please enter a ticket price");
        return;
      }

      if (isNaN(priceNumber) || priceNumber <= 0) {
        toast.error("Please enter a valid ticket price (greater than 0)");
        return;
      }
    }

    if (!newTicket.slotsAllocated || parseInt(newTicket.slotsAllocated) <= 0) {
      toast.error("Please enter valid slots allocated");
      return;
    }

    if (!trimmedDescription) {
      toast.error("Please enter a ticket description");
      return;
    }

    if (!newTicket.whatsIncluded || newTicket.whatsIncluded.length === 0) {
      toast.error("Please add at least one item to 'What's Included'");
      return;
    }

    const newTotal = totalAllocatedSlots + parseInt(newTicket.slotsAllocated);
    if (hasSlots && newTotal > parseInt(overallSlots)) {
      toast.error(
        `Adding ${newTicket.slotsAllocated} slots would exceed the overall slots. Remaining slots: ${remainingSlots}`
      );
      return;
    }

    const ticket = {
      name: trimmedName,
      price: isFree ? 0 : priceNumber,
      currency: ensureCurrency(newTicket.currency), // Always ensure valid currency
      slotsAllocated: parseInt(newTicket.slotsAllocated),
      description: trimmedDescription,
      whatsIncluded: [...newTicket.whatsIncluded],
    };

    const updatedTickets = [...ticketTypes, ticket];
    onTicketTypesChange(updatedTickets);
    setNewTicket({
      name: "",
      pricingMode: "priced",
      previousPricedPrice: "",
      price: "",
      currency: "GHS",
      slotsAllocated: "",
      description: "",
      whatsIncluded: [],
    });
    setNewIncludedItem("");
    toast.success("Ticket type added successfully");
  };

  const handleKeyPressIncluded = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddIncludedItem();
    }
  };

  const handleRemoveTicket = (index) => {
    const updatedTickets = ticketTypes.filter((_, i) => i !== index);
    onTicketTypesChange(updatedTickets);
    toast.success("Ticket type removed");
  };

  const handleStartEdit = (index) => {
    const ticket = ticketTypes[index];
    const ticketPriceNumber = parseFloat(ticket.price);
    const isFree = ticketPriceNumber === 0;
    setNewTicket({
      name: ticket.name || "",
      pricingMode: isFree ? "free" : "priced",
      previousPricedPrice: isFree ? "" : ticket.price?.toString() || "",
      price: isFree ? "0" : ticket.price?.toString() || "",
      currency: getTicketCurrency(ticket), // Get currency with backward compatibility
      slotsAllocated: ticket.slotsAllocated?.toString() || "",
      description: ticket.description || "",
      whatsIncluded: Array.isArray(ticket.whatsIncluded)
        ? [...ticket.whatsIncluded]
        : [],
    });
    setEditingIndex(index);
    setNewIncludedItem("");
    // Scroll to form
    setTimeout(() => {
      const formElement = document.querySelector('[data-ticket-form]');
      if (formElement) {
        formElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
    setNewTicket({
      name: "",
      pricingMode: "priced",
      previousPricedPrice: "",
      price: "",
      currency: "GHS",
      slotsAllocated: "",
      description: "",
      whatsIncluded: [],
    });
    setNewIncludedItem("");
  };

  const handleUpdateTicket = () => {
    const trimmedName = newTicket.name.trim();
    const trimmedDescription = newTicket.description.trim();
    const isFree = newTicket.pricingMode === "free";
    const priceNumber = parseFloat(newTicket.price);

    if (!trimmedName) {
      toast.error("Please enter a ticket name");
      return;
    }

    if (isFree) {
      // Backend expects `price`; for free tickets we always send 0.
      if (isNaN(priceNumber) || priceNumber !== 0) {
        toast.error("Free tickets must have a price of 0");
        return;
      }
    } else {
      if (newTicket.price === "" || newTicket.price === null || newTicket.price === undefined) {
        toast.error("Please enter a ticket price");
        return;
      }

      if (isNaN(priceNumber) || priceNumber <= 0) {
        toast.error("Please enter a valid ticket price (greater than 0)");
        return;
      }
    }

    if (!newTicket.slotsAllocated || parseInt(newTicket.slotsAllocated) <= 0) {
      toast.error("Please enter valid slots allocated");
      return;
    }

    if (!trimmedDescription) {
      toast.error("Please enter a ticket description");
      return;
    }

    if (!newTicket.whatsIncluded || newTicket.whatsIncluded.length === 0) {
      toast.error("Please add at least one item to 'What's Included'");
      return;
    }

    const oldTicket = ticketTypes[editingIndex];
    const oldSlots = parseInt(oldTicket.slotsAllocated) || 0;
    const newSlots = parseInt(newTicket.slotsAllocated);
    
    // Calculate total slots excluding the ticket being edited
    const slotsWithoutEditing = totalAllocatedSlots - oldSlots;
    const newTotal = slotsWithoutEditing + newSlots;
    
    if (hasSlots && newTotal > parseInt(overallSlots)) {
      toast.error(
        `Updating to ${newTicket.slotsAllocated} slots would exceed the overall slots. Remaining slots: ${remainingSlots + oldSlots}`
      );
      return;
    }

    const updatedTicket = {
      name: trimmedName,
      price: isFree ? 0 : priceNumber,
      currency: ensureCurrency(newTicket.currency), // Always ensure valid currency
      slotsAllocated: newSlots,
      description: trimmedDescription,
      whatsIncluded: [...newTicket.whatsIncluded],
    };

    const updatedTickets = [...ticketTypes];
    updatedTickets[editingIndex] = updatedTicket;
    onTicketTypesChange(updatedTickets);
    
    handleCancelEdit();
    toast.success("Ticket type updated successfully");
  };

  return (
    <div className="space-y-6">
      <div className="p-4 bg-white rounded-xl border border-gray-100 shadow-sm sm:rounded-2xl">
        <div className="flex items-center mb-4 space-x-2">
          <Users className="w-5 h-5 text-green-600" />
          <h3 className="text-lg font-bold text-gray-900">
            Overall Event Slots
          </h3>
        </div>

        <p className="mb-4 text-xs text-gray-600">
          Specify the total number of slots (tickets) available for your event.
          This will be distributed across different ticket types.
        </p>

        <div className="relative">
          <Users className="absolute left-3 top-1/2 w-4 h-4 text-gray-400 -translate-y-1/2" />
          <input
            type="number"
            value={overallSlots}
            onChange={(e) => {
              const value = e.target.value.replace(/[^0-9]/g, "");
              onOverallSlotsChange(value);
            }}
            placeholder="Enter total number of slots..."
            min="1"
            className="w-full pl-10 pr-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all bg-gray-50 focus:bg-white"
          />
        </div>
      </div>

      {/* Slots Summary */}
      {hasSlots && (
        <div
          className={`p-4 rounded-xl border-2 ${
            isSlotsBalanced
              ? "bg-green-50 border-green-300"
              : "bg-orange-50 border-orange-300"
          }`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Calculator className="w-5 h-5 text-gray-600" />
              <div>
                <p className="text-sm font-semibold text-gray-900">
                  Slots Allocation Summary
                </p>
                <p className="text-xs text-gray-600">
                  Overall: {overallSlots} | Allocated: {totalAllocatedSlots} |
                  Remaining: {remainingSlots}
                </p>
              </div>
            </div>
            {isSlotsBalanced ? (
              <div className="flex items-center space-x-1 text-green-700">
                <CheckCircle2 className="w-5 h-5" />
                <span className="text-sm font-semibold">Balanced</span>
              </div>
            ) : (
              <div className="flex items-center space-x-1 text-orange-700">
                <AlertCircle className="w-5 h-5" />
                <span className="text-sm font-semibold">
                  {remainingSlots > 0
                    ? `${remainingSlots} remaining`
                    : "Exceeded"}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Ticket Types Section */}
      <div className="p-4 bg-white rounded-xl border border-gray-100 shadow-sm sm:rounded-2xl">
        <div className="flex items-center mb-4 space-x-2">
          <Ticket className="w-5 h-5 text-green-600" />
          <h3 className="text-lg font-bold text-gray-900">Ticket Types</h3>
          <span className="px-2 py-0.5 text-xs font-semibold text-green-700 bg-green-100 rounded-full">
            {ticketTypes.length}
          </span>
        </div>

        <p className="mb-4 text-xs text-gray-600">
          Create different ticket types with varying prices and benefits. The
          total slots allocated across all ticket types must equal the overall
          slots.
        </p>

        {/* Add/Edit Ticket Form */}
        <div className="p-4 mb-4 bg-gray-50 rounded-lg border border-gray-200" data-ticket-form>
          {editingIndex !== null && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Edit2 className="w-4 h-4 text-blue-600" />
                  <p className="text-sm font-semibold text-blue-900">
                    Editing: {ticketTypes[editingIndex]?.name}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  className="text-xs font-medium text-blue-700 hover:text-blue-900 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
          <div className="space-y-3">
            {/* Ticket Name */}
            <div>
              <label className="block mb-1.5 text-xs font-semibold text-gray-700">
                Ticket Name <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Ticket className="absolute left-3 top-1/2 w-4 h-4 text-gray-400 -translate-y-1/2" />
                <input
                  type="text"
                  value={newTicket.name}
                  onChange={(e) => handleInputChange("name", e.target.value)}
                  placeholder="e.g., VIP, General Admission, Early Bird"
                  className="w-full pl-10 pr-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all bg-white"
                />
              </div>
            </div>

            {/* Price and Slots Allocated */}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <label className="block mb-1.5 text-xs font-semibold text-gray-700">
                  Price <span className="text-red-500">*</span>
                </label>
                <div className="mb-2 flex gap-2">
                  <button
                    type="button"
                    onClick={() => setTicketPricingMode("priced")}
                    className={`flex-1 px-3 py-2.5 text-sm font-semibold rounded-lg transition-all border ${
                      newTicket.pricingMode === "priced"
                        ? "bg-green-50 border-green-300 text-green-800"
                        : "bg-white border-gray-200 text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    Priced
                  </button>
                  <button
                    type="button"
                    onClick={() => setTicketPricingMode("free")}
                    className={`flex-1 px-3 py-2.5 text-sm font-semibold rounded-lg transition-all border ${
                      newTicket.pricingMode === "free"
                        ? "bg-green-50 border-green-300 text-green-800"
                        : "bg-white border-gray-200 text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    Free
                  </button>
                </div>

                <div className="flex gap-2">
                  {/* Currency Selector */}
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setCurrencyDropdownOpen(!currencyDropdownOpen)}
                      className="flex items-center justify-center px-3 py-2.5 text-sm font-semibold text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all min-w-[100px]"
                    >
                      <span>{CURRENCIES[newTicket.currency]?.symbol || "₵"}</span>
                      <ChevronDown className="ml-1 w-4 h-4" />
                    </button>
                    {currencyDropdownOpen && (
                      <>
                        <div
                          className="fixed inset-0 z-10"
                          onClick={() => setCurrencyDropdownOpen(false)}
                        />
                        <div className="absolute top-full left-0 z-20 mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg">
                          {Object.values(CURRENCIES).map((currency) => (
                            <button
                              key={currency.code}
                              type="button"
                              onClick={() => {
                                setNewTicket((prev) => ({ ...prev, currency: currency.code }));
                                setCurrencyDropdownOpen(false);
                              }}
                              className={`w-full px-4 py-2.5 text-left text-sm transition-colors ${
                                newTicket.currency === currency.code
                                  ? "bg-green-50 text-green-700 font-semibold"
                                  : "text-gray-700 hover:bg-gray-50"
                              }`}
                            >
                              <div className="flex items-center justify-between">
                                <span>{currency.symbol} {currency.name}</span>
                                {newTicket.currency === currency.code && (
                                  <CheckCircle2 className="w-4 h-4" />
                                )}
                              </div>
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                  {/* Price Input */}
                  <div className="relative flex-1">
                    {/* <DollarSign className="absolute left-3 top-1/2 w-4 h-4 text-gray-400 -translate-y-1/2" /> */}
                    <input
                      type="text"
                      value={newTicket.price}
                      onChange={(e) => handleInputChange("price", e.target.value)}
                      placeholder="0.00"
                      className="w-full pl-10 pr-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all bg-white"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block mb-1.5 text-xs font-semibold text-gray-700">
                  Slots Allocated <span className="text-red-500">*</span>
                  {hasSlots && remainingSlots > 0 && (
                    <span className="ml-2 text-xs font-normal text-green-600">
                      (Max: {remainingSlots + totalAllocatedSlots})
                    </span>
                  )}
                </label>
                <div className="relative">
                  <Users className="absolute left-3 top-1/2 w-4 h-4 text-gray-400 -translate-y-1/2" />
                  <input
                    type="text"
                    value={newTicket.slotsAllocated}
                    onChange={(e) =>
                      handleInputChange("slotsAllocated", e.target.value)
                    }
                    placeholder="0"
                    max={
                      hasSlots
                        ? remainingSlots + totalAllocatedSlots
                        : undefined
                    }
                    className="w-full pl-10 pr-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all bg-white"
                  />
                </div>
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="block mb-1.5 text-xs font-semibold text-gray-700">
                Description <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <FileText className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                <textarea
                  value={newTicket.description}
                  onChange={(e) =>
                    handleInputChange("description", e.target.value)
                  }
                  placeholder="Describe this ticket type..."
                  rows={3}
                  className="w-full pl-10 pr-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all bg-white resize-y"
                />
              </div>
            </div>

            {/* What's Included */}
            <div>
              <label className="block mb-1.5 text-xs font-semibold text-gray-700">
                What's Included <span className="text-red-500">*</span>
                <span className="ml-2 text-xs font-normal text-gray-500">
                  (Add items one by one)
                </span>
              </label>

              {/* Add Included Item Input */}
              <div className="flex gap-2 mb-3">
                <div className="flex-1 relative">
                  <Gift className="absolute left-3 top-1/2 w-4 h-4 text-gray-400 -translate-y-1/2" />
                  <input
                    type="text"
                    value={newIncludedItem}
                    onChange={(e) => setNewIncludedItem(e.target.value)}
                    onKeyPress={handleKeyPressIncluded}
                    placeholder="e.g., Access to all sessions, Free parking..."
                    className="w-full pl-10 pr-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all bg-white"
                  />
                </div>
                <button
                  type="button"
                  onClick={handleAddIncludedItem}
                  disabled={!newIncludedItem.trim()}
                  className="flex items-center space-x-1 px-4 py-2.5 text-sm font-semibold text-white bg-green-600 hover:bg-green-700 rounded-lg transition-all shadow-sm hover:shadow disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Plus className="w-4 h-4" />
                  <span>Add</span>
                </button>
              </div>

              {/* Included Items List */}
              {newTicket.whatsIncluded.length > 0 ? (
                <div className="space-y-2">
                  <div className="flex flex-wrap gap-2">
                    {newTicket.whatsIncluded.map((item, index) => (
                      <div
                        key={index}
                        className="flex items-center space-x-2 px-3 py-1.5 bg-green-50 border border-green-200 rounded-lg group hover:bg-green-100 transition-colors"
                      >
                        <Gift className="w-3.5 h-3.5 text-green-600" />
                        <span className="text-xs font-medium text-gray-900">
                          {item}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleRemoveIncludedItem(index)}
                          className="p-0.5 text-red-600 rounded transition-colors hover:bg-red-50"
                          title="Remove item"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="py-3 text-center bg-gray-50 rounded-lg border border-dashed border-gray-200">
                  <p className="text-xs text-gray-500">
                    No items added yet. Add items using the input above.
                  </p>
                </div>
              )}
            </div>

            {/* Add/Update Button */}
            {editingIndex !== null ? (
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  className="flex items-center justify-center space-x-2 px-4 py-2.5 flex-1 text-sm font-semibold text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg transition-all shadow-sm hover:shadow disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <X className="w-4 h-4" />
                  <span>Cancel</span>
                </button>
                <button
                  type="button"
                  onClick={handleUpdateTicket}
                  disabled={
                    !newTicket.name.trim() ||
                    !newTicket.price ||
                    (newTicket.pricingMode === "priced" &&
                      (parseFloat(newTicket.price) <= 0 || isNaN(parseFloat(newTicket.price)))) ||
                    (newTicket.pricingMode === "free" &&
                      (parseFloat(newTicket.price) !== 0 || isNaN(parseFloat(newTicket.price)))) ||
                    !newTicket.slotsAllocated ||
                    parseInt(newTicket.slotsAllocated) <= 0 ||
                    !newTicket.description.trim() ||
                    !newTicket.whatsIncluded ||
                    newTicket.whatsIncluded.length === 0
                  }
                  className="flex items-center justify-center space-x-2 px-4 py-2.5 flex-1 text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 rounded-lg transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Save className="w-4 h-4" />
                  <span>Update Ticket Type</span>
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={handleAddTicket}
                disabled={
                  !newTicket.name.trim() ||
                  !newTicket.price ||
                  (newTicket.pricingMode === "priced" &&
                    (parseFloat(newTicket.price) <= 0 || isNaN(parseFloat(newTicket.price)))) ||
                  (newTicket.pricingMode === "free" &&
                    (parseFloat(newTicket.price) !== 0 || isNaN(parseFloat(newTicket.price)))) ||
                  !newTicket.slotsAllocated ||
                  parseInt(newTicket.slotsAllocated) <= 0 ||
                  !newTicket.description.trim() ||
                  !newTicket.whatsIncluded ||
                  newTicket.whatsIncluded.length === 0 ||
                  (hasSlots &&
                    totalAllocatedSlots +
                      parseInt(newTicket.slotsAllocated || 0) >
                      parseInt(overallSlots))
                }
                className="flex items-center justify-center space-x-2 px-4 py-2.5 w-full text-sm font-semibold text-white bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 rounded-lg transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Plus className="w-4 h-4" />
                <span>Add Ticket Type</span>
              </button>
            )}
          </div>
        </div>

        {/* Ticket Types List */}
        {ticketTypes.length > 0 ? (
          <div className="space-y-3">
            <p className="text-xs font-medium text-gray-700">
              Added Ticket Types ({ticketTypes.length}):
            </p>
            <div className="space-y-3">
              {ticketTypes.map((ticket, index) => (
                <div
                  key={index}
                  className="p-4 bg-green-50 border border-green-200 rounded-lg group hover:bg-green-100 transition-colors"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <div className="flex-shrink-0 flex items-center justify-center w-8 h-8 bg-green-600 rounded-full">
                          <Ticket className="w-4 h-4 text-white" />
                        </div>
                        <h4 className="text-base font-bold text-gray-900">
                          {ticket.name}
                        </h4>
                        <span className="px-2 py-1 text-xs font-semibold text-green-700 bg-green-200 rounded">
                          {ticket.slotsAllocated} slots
                        </span>
                      </div>
                      <div className="flex items-center space-x-4 ml-10 mb-2">
                        <div className="flex items-center space-x-1.5 text-sm font-semibold text-green-700">
                          <Banknote className="w-4 h-4" />
                          {parseFloat(ticket.price) === 0 ? (
                            <span>Free</span>
                          ) : (
                            <span>{formatPrice(ticket.price, getTicketCurrency(ticket))}</span>
                          )}
                        </div>
                      </div>
                      <div className="ml-10 space-y-2">
                        <div>
                          <p className="text-xs font-medium text-gray-700 mb-1">
                            Description:
                          </p>
                          <p className="text-xs text-gray-600 leading-relaxed">
                            {ticket.description}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs font-medium text-gray-700 mb-2">
                            What's Included:
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {Array.isArray(ticket.whatsIncluded) ? (
                              ticket.whatsIncluded.map((item, itemIndex) => (
                                <div
                                  key={itemIndex}
                                  className="flex items-center space-x-1.5 px-2.5 py-1 bg-white border border-green-300 rounded-md"
                                >
                                  <Gift className="w-3 h-3 text-green-600" />
                                  <span className="text-xs text-gray-700">
                                    {item}
                                  </span>
                                </div>
                              ))
                            ) : (
                              <span className="text-xs text-gray-600">
                                {ticket.whatsIncluded}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        type="button"
                        onClick={() => handleStartEdit(index)}
                        disabled={editingIndex !== null}
                        className="flex-shrink-0 p-2 text-blue-600 bg-white rounded-lg border border-blue-200 transition-colors hover:bg-blue-50 hover:border-blue-300 disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Edit ticket type"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleRemoveTicket(index)}
                        disabled={editingIndex !== null}
                        className="flex-shrink-0 p-2 text-red-600 bg-white rounded-lg border border-red-200 transition-colors hover:bg-red-50 hover:border-red-300 disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Remove ticket type"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="py-8 text-center bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
            <Ticket className="w-12 h-12 mx-auto mb-3 text-gray-400" />
            <p className="text-sm font-medium text-gray-600">
              No ticket types added yet
            </p>
            <p className="mt-1 text-xs text-gray-500">
              Start adding ticket types using the form above
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TicketTypesSection;
